import { useMemo } from 'react'
import { Role } from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import type { PaletteItem, PaletteTarget } from 'common-ui/components/SearchPalette'
import type { SidebarMenuItemProps } from 'common-ui/components/SidebarMenuItem'
import type { LocalPaletteSource, RemotePaletteSource } from 'common-ui/hooks/usePaletteSearch'

import type { BillingServerApi } from '../../api/billing/billing-api'
import { useBillingApi } from '../../api/billing/billing-context'
import {
    makeAiConversationPath,
    makeApplianceDetailsPath,
    makeEditCustomerPath,
    makeEditLicensePath,
    makeEditUserPath,
    makeExternalUserPath,
    makeInstallationDetailsPath,
    Path,
} from '../../routes/path'

const RESULT_LIMIT = 3

const navigateTo = (to: string): PaletteTarget => ({ kind: 'navigate', to })

/** The list page with its table search box filled in, as `Table` reads it from the URL. */
const filteredList = (path: string, term: string) =>
    navigateTo(`${path}?${new URLSearchParams({ searchString: term })}`)

const joinDetails = (...details: (string | null | undefined)[]) => details.filter(Boolean).join(' · ') || undefined

interface EntitySource extends RemotePaletteSource {
    /** The lowest role that can open the details page of a result */
    minimumAllowedRole: Role
}

const entitySources = (api: BillingServerApi): EntitySource[] => [
    {
        id: 'customers',
        label: 'Customers',
        // Customer details are the edit page, which needs admin; license admins only see the list.
        minimumAllowedRole: Role.admin,
        search: async (term, signal) => {
            const { items, total } = await api.listCustomers(
                { filter: { searchName: term, excludeDeleted: true }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((customer) => ({
                    id: customer.id,
                    label: customer.name,
                    secondary: customer.customerNumber || undefined,
                    target: navigateTo(makeEditCustomerPath(customer.id)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.customers, term),
    },
    {
        id: 'licenses',
        label: 'Licenses',
        minimumAllowedRole: Role.licenseAdmin,
        search: async (term, signal) => {
            const { items, total } = await api.listLicenses(
                { filter: { searchName: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                // Licenses match on their key, but the key never appears in a result.
                items: items.map((license) => ({
                    id: license.id,
                    label: license.customer.name,
                    secondary: joinDetails(license.purpose, license.domain),
                    target: navigateTo(makeEditLicensePath(license.id)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.licenses, term),
    },
    {
        id: 'installations',
        label: 'Installations',
        minimumAllowedRole: Role.invoiceOnly,
        search: async (term, signal) => {
            const { items, total } = await api.listInstallations(
                { filter: { searchName: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((installation) => ({
                    id: installation.id,
                    label: installation.host,
                    secondary: joinDetails(installation.customer.name, installation.releaseTag),
                    target: navigateTo(
                        makeInstallationDetailsPath({
                            customerId: installation.customer.id,
                            installationId: installation.id,
                        })
                    ),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.installations, term),
    },
    {
        id: 'appliances',
        label: 'Appliances',
        minimumAllowedRole: Role.invoiceOnly,
        search: async (term, signal) => {
            const { items, total } = await api.listAppliances(
                { filter: { searchName: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((appliance) => ({
                    id: appliance.serialNumber,
                    label: appliance.serialNumber,
                    secondary: joinDetails(appliance.customer.name, appliance.productSku),
                    target: navigateTo(makeApplianceDetailsPath(appliance.serialNumber)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.appliances, term),
    },
    {
        id: 'internal-users',
        label: 'Internal users',
        minimumAllowedRole: Role.licenseAdmin,
        search: async (term, signal) => {
            const { items, total } = await api.listUsers(
                { filter: { username: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((user) => ({
                    id: user.id,
                    label: user.username,
                    target: navigateTo(makeEditUserPath(user.id)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.internalUsers, term),
    },
    {
        id: 'external-users',
        label: 'External users',
        minimumAllowedRole: Role.licenseAdmin,
        // The users-only list: invitations have no details page to open.
        search: async (term, signal) => {
            const { items, total } = await api.listExternalUsers(
                { filter: { username: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((user) => ({
                    id: user.id,
                    label: user.username,
                    secondary: joinDetails(...user.customerAccess.map(({ customer }) => customer.name)),
                    target: navigateTo(makeExternalUserPath(user.id)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.externalUsers, term),
    },
    {
        id: 'ai-conversations',
        label: 'AI conversations',
        minimumAllowedRole: Role.super,
        search: async (term, signal) => {
            const { items, total } = await api.listAiConversations(
                { filter: { searchName: term }, skip: 0, limit: RESULT_LIMIT },
                { signal }
            )
            return {
                items: items.map((conversation) => ({
                    id: conversation.id,
                    label: conversation.title || 'Untitled conversation',
                    secondary: joinDetails(conversation.customerName, conversation.installationHost),
                    target: navigateTo(makeAiConversationPath(conversation.installationId, conversation.threadId)),
                })),
                total,
            }
        },
        viewAll: (term) => filteredList(Path.ai, term),
    },
]

const createActions: { label: string; url: Path; minimumAllowedRole: Role }[] = [
    { label: 'Create customer', url: Path.createCustomer, minimumAllowedRole: Role.admin },
    { label: 'Create license', url: Path.createLicense, minimumAllowedRole: Role.licenseAdmin },
    { label: 'Create internal user', url: Path.createUser, minimumAllowedRole: Role.licenseAdmin },
    { label: 'Create external user', url: Path.createExternalUser, minimumAllowedRole: Role.licenseAdmin },
]

const pageItems = (menuItems: SidebarMenuItemProps[], profileUrl: string | undefined): PaletteItem[] => {
    // The menu here names every page with plain text; element names have no searchable label.
    const pages = menuItems.flatMap(({ name, url, children }): { label: string; url: string }[] => {
        if (typeof name !== 'string') return []
        if (!children) return url ? [{ label: name, url }] : []
        return children.flatMap((child) =>
            child?.url && typeof child.name === 'string' ? [{ label: `${name} › ${child.name}`, url: child.url }] : []
        )
    })
    if (profileUrl) pages.push({ label: 'Profile', url: profileUrl })
    return pages.map(({ label, url }) => ({ id: url, label, target: navigateTo(url) }))
}

/**
 * Billing Manager's global search: the sidebar pages and create actions for the user's role, then every entity
 * whose details page the role can open.
 */
export const useBillingSearchSources = (
    role: Role,
    menuItems: SidebarMenuItemProps[],
    profileUrl: string | undefined
) => {
    const api = useBillingApi()

    return useMemo(() => {
        const allowed = ({ minimumAllowedRole }: { minimumAllowedRole: Role }) =>
            isRoleAuthorized({ role, minimumAllowedRole })
        const localSources: LocalPaletteSource[] = [
            { id: 'navigate', label: 'Navigate', items: pageItems(menuItems, profileUrl) },
            {
                id: 'create',
                label: 'Create',
                items: createActions
                    .filter(allowed)
                    .map(({ label, url }) => ({ id: url, label, keywords: ['new', 'add'], target: navigateTo(url) })),
            },
        ]
        const remoteSources: RemotePaletteSource[] = entitySources(api).filter(allowed)
        return { localSources, remoteSources }
    }, [api, role, menuItems, profileUrl])
}
